import { AxiosResponse } from "axios";
import { ApiResponse } from "middleware/api";
import { actions } from "reducers/slots";
import { actions as notificationActions } from "../reducers/notification";
import * as regionsMiddleware from "middleware/regions";
import * as slotsMiddleware from "middleware/slots";

import {
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  takeLatest,
} from "redux-saga/effects";
import {
  RegionsResponseType,
  WorkzonesResponseType,
  RegionType,
} from "./types/regions";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  SlotBulkDeleteResponse,
  SlotsResponseType,
  SlotsType,
  StatusResponseType,
  StatusType,
} from "./types/slots";
import { SlotsFilterType, SlotsRequestParamsType } from "reducers/types/slots";
import moment from "moment";
import { DATE_FORMATS } from "utils/constants";
import { store } from "../store";
import i18next from "config/i18next";
import { createCSV } from "utils/files";
import { connectionErrorAlert } from "utils/alerts";
import { serverErrorAlert } from "utils/alerts";

const sagas: ForkEffect<never>[] = [
  takeLatest(actions.getRegionsRequest.type, getRegions),
  takeLatest(actions.getWorkzonesFetch.type, getWorkzones),
  takeLatest(actions.getStatusRequest.type, getStatus),
  takeLatest(actions.getSlotsRequest.type, getSlots),
  takeLatest(actions.exportSlotsRequest.type, exportSlots),
  takeLatest(actions.deleteBulkSlotDetailRequest.type, deleteBulkSlots),
];

export default sagas;

const formatParams = (payload: SlotsFilterType) => {
  const params: SlotsRequestParamsType = {
    regionId: payload.region ? payload.region : undefined,
    workZones: payload.workzone ? payload.workzone : undefined,
    startAt: moment(payload.date.from, DATE_FORMATS.extraShortDate).format(
      DATE_FORMATS.shortISODate
    ),
    finishAt: moment(payload.date.until, DATE_FORMATS.extraShortDate).format(
      DATE_FORMATS.shortISODate
    ),
    slotType: payload.shift || undefined,
    state: payload.status ? payload.status : undefined,
    limit: payload.limit,
    offset: payload.offset,
  };
  return params;
};

function* getRegions(): Generator<
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ payload: RegionType[]; type: string }>
  | CallEffect<AxiosResponse<ApiResponse<RegionsResponseType>>>,
  void,
  ApiResponse<RegionsResponseType>
> {
  const response = yield call(regionsMiddleware.getRegions);
  if (response.status !== 200) {
    yield put(actions.getRegionsError());
  } else {
    const { result } = response.data;
    yield put(actions.getRegionsSuccess(result.items));
  }
}

function* getWorkzones({
  payload,
}: PayloadAction<string>): Generator<
  | PutEffect<{ type: string }>
  | CallEffect<AxiosResponse<ApiResponse<WorkzonesResponseType>>>,
  void,
  ApiResponse<WorkzonesResponseType>
> {
  const response = yield call(regionsMiddleware.getWorkzonesByRegion, payload);
  if (response.status !== 200) {
    yield put(actions.getWorkzonesError());
  } else {
    const { result } = response.data;
    yield put(actions.getWorkzonesSuccess(result.items));
  }
}

function* getStatus(): Generator<
  | PutEffect<{ type: string }>
  | PutEffect<{ payload: StatusType[]; type: string }>
  | CallEffect<AxiosResponse<ApiResponse<StatusResponseType>>>,
  void,
  ApiResponse<StatusResponseType>
> {
  const response = yield call(slotsMiddleware.getStatus);
  if (response.status !== 200) {
    yield put(actions.getStatusError());
  } else {
    const { result } = response.data;
    yield put(actions.getStatusSuccess(result.items));
  }
}
function* getSlots({
  payload,
}: PayloadAction<SlotsFilterType>): Generator<
  CallEffect<AxiosResponse<SlotsResponseType>> | PutEffect<{ type: string }>,
  void,
  ApiResponse<SlotsResponseType>
> {
  const params = formatParams(payload);
  const response = yield call(slotsMiddleware.getSlots, params);
  if (response.status !== 200) {
    yield put(actions.getSlotsError());
    yield put(notificationActions.showNotification(serverErrorAlert()));
  } else {
    yield put(actions.getSlotsSuccess(response.data));
  }
}

function* exportSlots({
  payload,
}: PayloadAction<SlotsFilterType>): Generator<
  CallEffect<AxiosResponse<SlotsResponseType>> | PutEffect<{ type: string }>,
  void,
  ApiResponse<SlotsResponseType>
> {
  const params = formatParams(payload);
  const response = yield call(slotsMiddleware.exportSlots, params);
  if (response.status !== 200) {
    yield put(actions.exportSlotsError());
    yield put(notificationActions.showNotification(connectionErrorAlert()));
  } else {
    createCSV(response.data, "slots");

    yield put(actions.exportSlotsSuccess());
    yield put(
      notificationActions.showNotification({
        id: "notification-export-success",
        level: "success",
        title: i18next.t("alerts:exportTitle"),
        body: i18next.t("alerts:exportMessage"),
      })
    );
  }
}

function* deleteBulkSlots({
  payload,
}: PayloadAction<SlotsType[]>): Generator<
  | CallEffect<AxiosResponse<SlotBulkDeleteResponse>>
  | PutEffect<{ type: string }>,
  void,
  ApiResponse<SlotBulkDeleteResponse>
> {
  const slots: number[] = payload?.map((slot) => slot.id);

  const response = yield call(slotsMiddleware.bulkDeleteSlot, slots);
  if (response.status !== 200 && response.status !== 201) {
    const { filters, filtersExtra } = store.getState().slots;
    const getSlots = () => {
      store.dispatch(
        actions.getSlotsRequest({
          ...filters,
          ...filtersExtra,
        })
      );
    };
    yield put(
      notificationActions.showNotification(connectionErrorAlert(getSlots))
    );
    yield put(actions.deleteBulkSlotDetailError());
  } else {
    const notDeleted = response.data.result.notDeleted;
    yield put(actions.setErrorSlots(notDeleted));
    const { filters, filtersExtra } = store.getState().slots;
    yield put(actions.deleteBulkSlotDetailSuccess());
    yield put(actions.getSlotsRequest({ ...filters, ...filtersExtra }));
  }
}
